import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from "@/components/ui/combobox";
import { useUser } from "@/hooks/useUser";
import { useMemo } from "react";
import { CircleUserRound } from "lucide-react";
import User from "../User";

const ALL_USERS_VALUE = "__all__";

interface UserItem {
  value: string;
  label: string;
  photo: string;
}

interface AssigneeDropdownProps {
  onSelect: (userId: string) => void;
  onAllSelect: () => void;
  value?: string;
}

/*
 * Shows a searchable combobox of users that can be selected.
 * An option "All Users" is shown at the top.
 * When a user is selected onSelect fires with the userId or
 * when All Users is selected onAllSelect is fired with no params.
 */

export const AssigneeDropdown = ({ onSelect, onAllSelect, value }: AssigneeDropdownProps) => {
  const { users } = useUser();

  const items: UserItem[] = useMemo(
    () => [
      { value: ALL_USERS_VALUE, label: "All Users", photo: "" },
      ...users.map((user) => ({
        value: user.lassoUserId,
        label: `${user.firstName} ${user.lastName}`,
        photo: user.photoURL ?? "",
      })),
    ],
    [users],
  );

  const selectedItem = useMemo(() => {
    if (!value?.length) return items[0];
    return items.find((item) => item.value === value) ?? items[0];
  }, [items, value]);

  return (
    <Combobox
      items={items}
      itemToStringValue={(item) => item.label}
      value={selectedItem}
      onValueChange={(item) => {
        if (!item) return;
        if (item.value === ALL_USERS_VALUE) {
          onAllSelect();
        } else {
          onSelect(item.value);
        }
      }}
    >
      <ComboboxTrigger render={<Button variant="secondary" data-testid="assignee_trigger" />}>
        <User name={selectedItem.label} icon={<CircleUserRound className="h-4 w-4" />} photo={selectedItem.photo} />
      </ComboboxTrigger>
      <ComboboxContent align="end">
        <ComboboxInput placeholder="Search users..." showTrigger={false} />
        <ComboboxEmpty>No users found.</ComboboxEmpty>
        <ComboboxList>
          {(item) => (
            <ComboboxItem key={item.value} value={item} data-testid={item.label.replace(/\s/g, "-")}>
              <User name={item.label} photo={item.photo} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
